/**
 * API呼び出し用カスタムフック
 *
 * Reactのカスタムフックは、コンポーネント間で再利用可能なロジックを共有する仕組み
 * LaravelでいうTraitやServiceクラスに近い概念
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { api, ApiError } from '../services/api';

/**
 * useApiの戻り値の型
 */
interface UseApiResult<T> {
  data: T | null;           // 取得したデータ
  loading: boolean;         // ローディング中かどうか
  error: string | null;     // エラーメッセージ
  refetch: () => void;      // データを再取得する関数
}

/**
 * GETリクエスト用のカスタムフック
 *
 * 使用例:
 * const { data, loading, error } = useApi<Facility[]>('/api/facilities');
 *
 * LaravelでいうControllerのindexメソッドのように、
 * データ取得・エラー処理・ローディング状態を一括で管理
 *
 * @param endpoint - APIエンドポイント
 * @param dependencies - 依存配列（この値が変わると再取得）
 */
export function useApi<T>(
  endpoint: string,
  dependencies: unknown[] = []
): UseApiResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestSeqRef = useRef(0);

  // データ取得関数
  const fetchData = useCallback(async () => {
    const requestSeq = ++requestSeqRef.current;
    setLoading(true);
    setError(null);

    try {
      const result = await api.get<T>(endpoint);
      if (requestSeq !== requestSeqRef.current) return;
      setData(result);
    } catch (err) {
      if (requestSeq !== requestSeqRef.current) return;
      if (err instanceof ApiError) {
        // APIエラーの場合
        if (err.status === 401) {
          setError('認証が必要です。ログインしてください。');
        } else if (err.status === 403) {
          setError('このリソースにアクセスする権限がありません。');
        } else if (err.status === 404) {
          setError('リソースが見つかりません。');
        } else {
          setError(err.message);
        }
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('予期しないエラーが発生しました');
      }
    } finally {
      if (requestSeq === requestSeqRef.current) {
        setLoading(false);
      }
    }
  }, [endpoint]);

  // コンポーネントマウント時とdependenciesが変わった時にデータ取得
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData, ...dependencies]);

  return { data, loading, error, refetch: fetchData };
}

/**
 * ミューテーション（POST/PUT/DELETE）用のカスタムフック
 *
 * 使用例:
 * const { mutate, loading, error } = useMutation<Patient>('/api/patients', 'POST');
 * await mutate({ name: '田中太郎' });
 *
 * LaravelでいうControllerのstore/update/destroyメソッドに相当
 */
interface UseMutationResult<T, B = unknown> {
  mutate: (body?: B) => Promise<T | null>;
  loading: boolean;
  error: string | null;
  reset: () => void;
}

export function useMutation<T, B = unknown>(
  endpoint: string,
  method: 'POST' | 'PUT' | 'DELETE' | 'PATCH' = 'POST'
): UseMutationResult<T, B> {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = async (body?: B): Promise<T | null> => {
    setLoading(true);
    setError(null);

    try {
      let result: T;
      switch (method) {
        case 'POST':
          result = await api.post<T>(endpoint, body);
          break;
        case 'PUT':
          result = await api.put<T>(endpoint, body);
          break;
        case 'DELETE':
          result = await api.delete<T>(endpoint);
          break;
        case 'PATCH':
          result = await api.patch<T>(endpoint, body);
          break;
      }
      return result;
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('予期しないエラーが発生しました');
      }
      return null;
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setError(null);
  };

  return { mutate, loading, error, reset };
}

export default useApi;
